import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check,
  CheckCheck,
  Download,
  Loader2,
  Mic,
  MessageSquare,
  Paperclip,
  Send,
  Square,
  X,
} from 'lucide-react';
import {
  PortalConversationRow,
  PortalMessage,
  PortalMessageAttachment,
  portalMentorService,
} from '../../../services/api';
import { Avatar, EmptyState, fa, fullName, inputClass, timeAgo } from './MentorUI';

interface Props {
  onError: (err: any, fallback: string) => void;
}

/** هر ۲۰ ثانیه صندوق و نخ باز تازه می‌شوند — بدون این، پیام تازه دیده نمی‌شود. */
const POLL_MS = 20000;

const MAX_ATTACHMENT_MB = 25;

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });

const dayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('fa-IR', { month: 'long', day: 'numeric' });

const humanSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? fa(Math.round((bytes / (1024 * 1024)) * 10) / 10) + ' مگابایت'
    : fa(Math.max(1, Math.round(bytes / 1024))) + ' کیلوبایت';

const duration = (sec?: number | null) => {
  if (!sec) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return fa(m) + ':' + String(s).padStart(2, '0').replace(/\d/g, (d) => fa(Number(d)));
};

// ─── پیوست ───────────────────────────────────────────────────────────────────

/**
 * پیوست را با احراز هویت می‌گیرد و به object URL تبدیل می‌کند.
 * لینک مستقیم کار نمی‌کند چون مسیر پشت JWT است و مرورگر هدر نمی‌فرستد.
 */
const Attachment: React.FC<{ file: PortalMessageAttachment; mine: boolean }> = ({ file, mine }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ویس و تصویر خودکار لود می‌شوند تا بشود همان‌جا پخش/دید؛ فایل با کلیک
  useEffect(() => {
    if (file.kind === 'FILE') return undefined;
    let revoked: string | null = null;
    let cancelled = false;
    portalMentorService
      .attachmentUrl(file.id)
      .then((objectUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        revoked = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [file.id, file.kind]);

  const download = async () => {
    setBusy(true);
    try {
      const objectUrl = await portalMentorService.attachmentUrl(file.id);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setBusy(false);
    }
  };

  if (file.kind === 'AUDIO') {
    return (
      <div className="mt-1 flex items-center gap-2">
        {url ? (
          <audio controls src={url} className="h-9 max-w-[240px]" />
        ) : (
          <span className="flex items-center gap-1.5 text-[11px] font-bold opacity-70">
            <Loader2 className="h-3 w-3 animate-spin" />
            در حال بارگذاری ویس…
          </span>
        )}
        {duration(file.durationSec) ? (
          <span className="text-[10.5px] font-bold opacity-60">{duration(file.durationSec)}</span>
        ) : null}
      </div>
    );
  }

  if (file.kind === 'IMAGE') {
    return url ? (
      <a href={url} target="_blank" rel="noreferrer" className="mt-1 block">
        <img
          src={url}
          alt={file.name}
          className="max-h-56 max-w-full rounded-lg border border-black/10"
        />
      </a>
    ) : (
      <span className="mt-1 block text-[11px] font-bold opacity-70">در حال بارگذاری تصویر…</span>
    );
  }

  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      className={
        'mt-1 flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition ' +
        (mine
          ? 'border-white/30 text-white hover:bg-white/10'
          : 'border-gray-300 text-gray-700 hover:bg-gray-50')
      }
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      <span className="max-w-[180px] truncate">{file.name}</span>
      <span className="opacity-60">{humanSize(file.sizeBytes)}</span>
    </button>
  );
};

// ─── حباب پیام ───────────────────────────────────────────────────────────────

const Bubble: React.FC<{ message: PortalMessage }> = ({ message }) => {
  // پیام تیم «مال من» است و سمت چپ می‌نشیند؛ پیام شرکت‌کننده سمت راست
  const mine = !message.fromParticipant;

  return (
    <div className={'flex ' + (mine ? 'justify-start' : 'justify-end')}>
      <div
        className={
          'max-w-[78%] rounded-2xl px-3 py-2 text-sm ' +
          (mine
            ? 'rounded-bl-sm bg-blue-600 text-white'
            : 'rounded-br-sm border border-gray-200 bg-white text-gray-800')
        }
      >
        {message.body ? (
          <p className="whitespace-pre-wrap break-words leading-6">{message.body}</p>
        ) : null}

        {message.attachments.map((file) => (
          <Attachment key={file.id} file={file} mine={mine} />
        ))}

        <div
          className={
            'mt-1 flex items-center justify-end gap-1 text-[10px] font-bold ' +
            (mine ? 'text-white/70' : 'text-gray-400')
          }
        >
          <span>{clock(message.createdAt)}</span>
          {mine ? (
            message.readAt ? (
              <CheckCheck className="h-3 w-3" aria-label="خوانده شد" />
            ) : (
              <Check className="h-3 w-3" aria-label="ارسال شد" />
            )
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ─── تب گفتگو ────────────────────────────────────────────────────────────────

export const MentorChatTab: React.FC<Props> = ({ onError }) => {
  const [rows, setRows] = useState<PortalConversationRow[]>([]);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<PortalMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);

  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<File | null>(null);
  const [sending, setSending] = useState(false);

  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);

  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const active = rows.find((r) => r.participantId === activeId) ?? null;

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const loadInbox = useCallback(async () => {
    try {
      setRows(await portalMentorService.conversations(query || undefined));
    } catch (err: any) {
      onError(err, 'دریافت گفتگوها انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [query, onError]);

  useEffect(() => {
    setLoading(true);
    loadInbox();
  }, [loadInbox]);

  const loadThread = useCallback(
    async (participantId: string, quiet = false) => {
      if (!quiet) setThreadLoading(true);
      try {
        const data = await portalMentorService.thread(participantId);
        setMessages(data.messages);
        setHasMore(data.hasMore);
      } catch (err: any) {
        onError(err, 'دریافت گفتگو انجام نشد.');
      } finally {
        if (!quiet) setThreadLoading(false);
      }
    },
    [onError],
  );

  useEffect(() => {
    if (!activeId) return;
    loadThread(activeId);
  }, [activeId, loadThread]);

  // تازه‌سازی دوره‌ای؛ نخ باز بی‌صدا به‌روز می‌شود تا اسکرول نپرد
  useEffect(() => {
    const timer = setInterval(() => {
      loadInbox();
      if (activeId) loadThread(activeId, true);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [activeId, loadInbox, loadThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  const loadOlder = async () => {
    if (!activeId || !messages.length) return;
    try {
      const data = await portalMentorService.thread(activeId, messages[0].id);
      setMessages((prev) => [...data.messages, ...prev]);
      setHasMore(data.hasMore);
    } catch (err: any) {
      onError(err, 'دریافت پیام‌های قدیمی‌تر انجام نشد.');
    }
  };

  // ── ضبط ویس ────────────────────────────────────────────────────────────
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      onError(null, 'مرورگر شما ضبط صدا را پشتیبانی نمی‌کند.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      startedAtRef.current = Date.now();

      recorder.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        const ext = (recorder.mimeType || 'audio/webm').includes('ogg') ? 'ogg' : 'webm';
        setPending(new File([blob], 'voice-' + Date.now() + '.' + ext, { type: blob.type }));
        setRecording(false);
      };

      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setRecordSecs(0);
    } catch {
      onError(null, 'دسترسی به میکروفون داده نشد.');
    }
  };

  useEffect(() => {
    if (!recording) return undefined;
    const timer = setInterval(
      () => setRecordSecs(Math.floor((Date.now() - startedAtRef.current) / 1000)),
      500,
    );
    return () => clearInterval(timer);
  }, [recording]);

  const stopRecording = () => recorderRef.current?.stop();

  const cancelRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    recorder.onstop = () => {
      recorder.stream.getTracks().forEach((t) => t.stop());
      setRecording(false);
    };
    recorder.stop();
    chunksRef.current = [];
  };

  const pickFile = (file: File | null) => {
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      onError(null, `حجم فایل باید کمتر از ${fa(MAX_ATTACHMENT_MB)} مگابایت باشد.`);
      return;
    }
    setPending(file);
  };

  const send = async () => {
    if (!activeId || (!draft.trim() && !pending)) return;
    setSending(true);
    try {
      const message = await portalMentorService.sendMessage(activeId, {
        body: draft.trim() || undefined,
        attachmentOnly: !draft.trim(),
      });

      if (pending) {
        const secs = pending.name.startsWith('voice-') ? recordSecs : undefined;
        const attachment = await portalMentorService.attachToMessage(message.id, pending, secs);
        message.attachments = [attachment];
      }

      setMessages((prev) => [...prev, message]);
      setDraft('');
      setPending(null);
      setRecordSecs(0);
      loadInbox();
    } catch (err: any) {
      onError(err, 'ارسال پیام انجام نشد.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <p className="py-10 text-center text-sm font-bold text-gray-400">در حال بارگذاری…</p>;
  }

  if (!rows.length && !query) {
    return (
      <EmptyState
        icon={<MessageSquare className="h-5 w-5" />}
        title="کسی به شما سپرده نشده"
        body="پس از اینکه مدیریت دوره شرکت‌کننده‌ای را به شما بسپارد، اینجا می‌توانید با او گفتگو کنید."
      />
    );
  }

  return (
    <div className="grid gap-3 lg:grid-cols-[320px_1fr]">
      {/* فهرست افراد */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 p-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجوی نام یا شماره…"
            className={inputClass}
          />
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          {!rows.length ? (
            <p className="py-8 text-center text-xs font-bold text-gray-400">کسی پیدا نشد.</p>
          ) : (
            rows.map((row) => (
              <button
                key={row.participantId}
                type="button"
                onClick={() => setActiveId(row.participantId)}
                className={
                  'flex w-full items-center gap-2.5 border-b border-gray-100 px-3 py-2.5 text-right transition last:border-b-0 ' +
                  (row.participantId === activeId ? 'bg-blue-50' : 'hover:bg-gray-50')
                }
              >
                <Avatar user={row.user} size="sm" tone="#1D4ED8" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-xs font-bold text-gray-900">
                      {fullName(row.user)}
                    </span>
                    {row.lastMessageAt ? (
                      <span className="shrink-0 text-[10px] font-bold text-gray-400">
                        {timeAgo(row.lastMessageAt)}
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-[10.5px] font-bold text-gray-400">
                      {row.lastMessage
                        ? (row.lastMessage.fromParticipant ? '' : 'شما: ') +
                          (row.lastMessage.body || 'پیوست')
                        : row.cohort?.name || 'بدون گفتگو'}
                    </span>
                    {row.unread ? (
                      <span className="shrink-0 rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-black text-white">
                        {fa(row.unread)}
                      </span>
                    ) : null}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* نخ گفتگو */}
      <div className="flex min-h-[560px] flex-col rounded-xl border border-gray-200 bg-gray-50">
        {!active ? (
          <div className="flex flex-1 items-center justify-center p-8 text-center">
            <p className="text-sm font-bold text-gray-400">
              یک نفر را از فهرست انتخاب کنید تا گفتگو باز شود.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2.5 rounded-t-xl border-b border-gray-200 bg-white px-4 py-3">
              <Avatar user={active.user} size="sm" tone="#1D4ED8" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-gray-900">{fullName(active.user)}</p>
                <p className="text-[10.5px] font-bold text-gray-400">
                  {active.cohort?.name || '—'} · {active.user.phone || 'بدون شماره'}
                </p>
              </div>
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto p-4">
              {threadLoading ? (
                <p className="py-8 text-center text-xs font-bold text-gray-400">
                  در حال بارگذاری…
                </p>
              ) : (
                <>
                  {hasMore ? (
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={loadOlder}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[11px] font-bold text-gray-600 hover:bg-gray-50"
                      >
                        پیام‌های قدیمی‌تر
                      </button>
                    </div>
                  ) : null}

                  {!messages.length ? (
                    <p className="py-8 text-center text-xs font-bold text-gray-400">
                      هنوز پیامی رد و بدل نشده. اولین پیام را شما بفرستید.
                    </p>
                  ) : (
                    messages.map((message, index) => {
                      const prev = messages[index - 1];
                      const newDay =
                        !prev ||
                        new Date(prev.createdAt).toDateString() !==
                          new Date(message.createdAt).toDateString();
                      return (
                        <React.Fragment key={message.id}>
                          {newDay ? (
                            <p className="py-1 text-center text-[10px] font-black text-gray-400">
                              {dayLabel(message.createdAt)}
                            </p>
                          ) : null}
                          <Bubble message={message} />
                        </React.Fragment>
                      );
                    })
                  )}
                </>
              )}
              <div ref={bottomRef} />
            </div>

            {/* نوشتن */}
            <div className="rounded-b-xl border-t border-gray-200 bg-white p-3">
              {pending ? (
                <div className="mb-2 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5">
                  <Paperclip className="h-3.5 w-3.5 text-blue-600" />
                  <span className="min-w-0 flex-1 truncate text-[11px] font-bold text-blue-800">
                    {pending.name}
                  </span>
                  <span className="text-[10px] font-bold text-blue-500">
                    {humanSize(pending.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPending(null)}
                    className="rounded p-0.5 text-blue-500 hover:bg-blue-100"
                    aria-label="حذف پیوست"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : null}

              {recording ? (
                <div className="mb-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-red-600" />
                  <span className="flex-1 text-[11px] font-bold text-red-700">
                    در حال ضبط… {duration(recordSecs) || '۰:۰۰'}
                  </span>
                  <button
                    type="button"
                    onClick={stopRecording}
                    className="flex items-center gap-1 rounded-lg bg-red-600 px-2.5 py-1 text-[11px] font-bold text-white"
                  >
                    <Square className="h-3 w-3" />
                    پایان
                  </button>
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="text-[11px] font-bold text-red-500 hover:text-red-700"
                  >
                    لغو
                  </button>
                </div>
              ) : null}

              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    pickFile(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={recording || sending}
                  className="rounded-lg border border-gray-300 p-2 text-gray-500 transition hover:bg-gray-50 disabled:opacity-40"
                  aria-label="پیوست فایل"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={recording ? stopRecording : startRecording}
                  disabled={sending || !!pending}
                  title={pending ? 'اول پیوست فعلی را بفرستید' : 'ضبط ویس'}
                  className={
                    'rounded-lg border p-2 transition disabled:opacity-40 ' +
                    (recording
                      ? 'border-red-300 bg-red-50 text-red-600'
                      : 'border-gray-300 text-gray-500 hover:bg-gray-50')
                  }
                  aria-label="ضبط ویس"
                >
                  <Mic className="h-4 w-4" />
                </button>

                <textarea
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="پیام بنویسید…  (Enter ارسال، Shift+Enter خط تازه)"
                  maxLength={4000}
                  className={inputClass + ' max-h-32 min-h-[40px] flex-1 resize-y'}
                />

                <button
                  type="button"
                  onClick={send}
                  disabled={sending || recording || (!draft.trim() && !pending)}
                  className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white transition disabled:opacity-50"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  ارسال
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
